import {Controller} from "@hotwired/stimulus"

// Shift+H → Home; Shift+D → Download dropdown; Shift+P → Page dropdown; Shift+S → Story dropdown.
// With the Page/Story dropdown open, a bare 1..9 jumps to that item; with Download open, Enter starts it.
// Down/Right and Up/Left move through visible page/story links; Enter opens the focused choice.
// Esc closes any nav dropdown and returns focus to its trigger.
// Shift+1..9 (dropdown closed) still jumps to a page directly; Ctrl/Cmd+Shift+1..9 to a story.
// Holding Shift swaps page icons for their shortcut number.
// ponytail: uses event.code (layout-independent) instead of @github/hotkey,
// which matches event.key so Shift+1 must be declared as "!" and breaks on non-US layouts.
export default class extends Controller {
  connect() {
    this.onKeydown = this.keydown.bind(this)
    this.onKeyup = this.keyup.bind(this)
    this.onBlur = () => this.toggleNumbers(false)
    window.addEventListener("keydown", this.onKeydown)
    window.addEventListener("keyup", this.onKeyup)
    window.addEventListener("blur", this.onBlur)
  }

  disconnect() {
    window.removeEventListener("keydown", this.onKeydown)
    window.removeEventListener("keyup", this.onKeyup)
    window.removeEventListener("blur", this.onBlur)
  }

  keydown(event) {
    if (event.defaultPrevented || event.isComposing) return
    if (event.key === "Escape") return this.closeDropdowns()
    if (this.navigateDropdown(event)) return
    // Enter while the Download dropdown is open → start the download.
    if (event.key === "Enter" && this.expanded("[data-shortcut-download]")) {
      const submit = document.querySelector("[data-shortcut-download-submit]")
      if (submit) { event.preventDefault(); submit.click() }
      return
    }
    if (event.target.closest("input, textarea, select, [contenteditable]")) return
    if (event.key === "Shift") this.toggleNumbers(true)

    // With a page/story dropdown open, a bare 1..9 picks that numbered item.
    const pagesOpen = this.expanded("[data-shortcut-pages]")
    const storiesOpen = this.expanded("[data-shortcut-stories]")
    if (pagesOpen || storiesOpen) {
      const digit = event.code.match(/^(?:Digit|Numpad)([1-9])$/)
      if (digit) {
        const link = document.querySelectorAll(pagesOpen ? "[data-shortcut-page]" : "[data-shortcut-story]")[parseInt(digit[1], 10) - 1]
        if (!link) return
        event.preventDefault()
        link.click()
        return
      }
    }

    if (!event.shiftKey) return

    // Shift+P / Shift+S open the page / story dropdown (Esc closes it).
    if (event.code === "KeyP" || event.code === "KeyS") {
      const btn = document.querySelector(event.code === "KeyP" ? "[data-shortcut-pages]" : "[data-shortcut-stories]")
      if (!btn) return
      event.preventDefault()
      if (btn.getAttribute("aria-expanded") !== "true") {
        this.closeDropdowns()
        btn.click()
      }
      const popup = btn.closest('[data-controller~="dropdown"]')?.querySelector('[data-dropdown-target="popup"]')
      const input = popup?.querySelector('input')
      if (input) input.focus({ preventScroll: true })
      else btn.focus({ preventScroll: true })
      return
    }
    if (event.code === "KeyH" || event.code === "KeyD") {
      const target = document.querySelector(event.code === "KeyH" ? "[data-shortcut-home]" : "[data-shortcut-download]")
      if (!target) return
      event.preventDefault()
      target.click()
      return
    }
    const match = event.code.match(/^(?:Digit|Numpad)([1-9])$/)
    if (!match) return
    const selector = (event.ctrlKey || event.metaKey) ? "[data-shortcut-story]" : "[data-shortcut-page]"
    const link = document.querySelectorAll(selector)[parseInt(match[1], 10) - 1]
    if (!link) return
    event.preventDefault()
    link.click()
  }

  keyup(event) {
    if (event.key === "Shift") this.toggleNumbers(false)
  }

  navigateDropdown(event) {
    if (!["ArrowDown", "ArrowUp", "ArrowRight", "ArrowLeft", "Enter"].includes(event.key) || event.ctrlKey || event.metaKey || event.altKey) return false
    // Keep horizontal cursor movement and text selection available in search fields.
    if (["ArrowLeft", "ArrowRight"].includes(event.key) && event.target.closest("input, textarea, select, [contenteditable]")) return false

    const trigger = this.element.querySelector('[data-shortcut-pages][aria-expanded="true"], [data-shortcut-stories][aria-expanded="true"]')
    const popup = trigger?.closest('[data-controller~="dropdown"]')?.querySelector('[data-dropdown-target="popup"]')
    if (!popup || (event.target !== trigger && !popup.contains(event.target))) return false

    const selector = trigger.hasAttribute('data-shortcut-pages') ? '[data-shortcut-page]' : '[data-shortcut-story]'
    const links = Array.from(popup.querySelectorAll(selector)).filter(link => link.getClientRects().length > 0 && link.getAttribute('aria-disabled') !== 'true')
    event.preventDefault()
    if (!links.length) return true

    const index = links.indexOf(document.activeElement)
    if (event.key === "Enter") {
      if (event.repeat) return true
      links[index < 0 ? 0 : index].click()
      this.closeDropdowns()
    } else {
      const forward = event.key === "ArrowDown" || event.key === "ArrowRight"
      const next = index < 0
        ? (forward ? 0 : links.length - 1)
        : (index + (forward ? 1 : -1) + links.length) % links.length
      links[next].focus({ preventScroll: true })
      links[next].scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }
    return true
  }

  expanded(selector) {
    const el = document.querySelector(selector)
    return !!el && el.getAttribute("aria-expanded") === "true"
  }

  // Esc: hide whichever nav dropdown (page/story/download) is open, via its dropdown controller.
  closeDropdowns() {
    document.querySelectorAll("[data-shortcut-pages], [data-shortcut-stories], [data-shortcut-download]").forEach(btn => {
      if (btn.getAttribute("aria-expanded") !== "true") return
      const el = btn.closest('[data-controller~="dropdown"]')
      if (el) this.application.getControllerForElementAndIdentifier(el, "dropdown")?.hide(null)
      btn.focus({ preventScroll: true })
    })
  }

  toggleNumbers(show) {
    this.element.querySelectorAll("[data-shortcut-icon]").forEach(el => el.classList.toggle("hidden", show))
    this.element.querySelectorAll("[data-shortcut-num]").forEach(el => el.classList.toggle("hidden", !show))
    // hints live outside the nav (header Home/Download), so search document-wide
    document.querySelectorAll("[data-shortcut-hint]").forEach(el => el.classList.toggle("hidden", !show))
  }
}
